import { MultiplierType } from 'pages/invoices/util/InvoicesPageUtil';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Control,
    UseFormGetValues,
    UseFormHandleSubmit,
    UseFormReset,
    UseFormSetValue,
    useWatch,
} from 'react-hook-form';
import { VariableSizeList } from 'react-window';
import { useUpdateInvoiceTimecardMutation } from 'store/api/invoice-details';
import { ClassifierData } from 'types/classifiers';
import {
    InvoiceMatterForm,
    InvoiceTimecard,
    InvoiceTimecardAssignment,
    InvoiceTimecardForm,
} from 'types/invoice-details';
import { InvoiceData } from 'types/invoices';
import { NamedEntityMinDto } from 'types/other';
import MeasuredRow from './component/MeasuredRow';
import InvoiceTimecardTableItem from './InvoiceTimecardTableItem';
import { SortField, TimecardSortState } from './InvoiceTimecardTableTotals';

const DEFAULT_ROW_HEIGHT = 120;
const MAX_LIST_HEIGHT = 600;
const OVERSCAN_COUNT = 5;

export function sortTimecardsByField(timecards: InvoiceTimecard[], sort: TimecardSortState): InvoiceTimecard[] {
    if (!sort.field) return timecards;
    return timecards.toSorted((a, b) => {
        let comparison = 0;
        if (sort.field === 'date') {
            comparison = a.timecardDate.localeCompare(b.timecardDate);
        } else if (sort.field === 'author') {
            comparison = (a.employee?.name || '').localeCompare(b.employee?.name || '');
        } else if (sort.field === ('billable' as SortField)) {
            comparison = (a.isNonBillable ? 1 : 0) - (b.isNonBillable ? 1 : 0);
        }
        return sort.direction === 'asc' ? comparison : -comparison;
    });
}

interface InvoiceTimecardAssignmentTableProps {
    assignment: InvoiceTimecardAssignment;
    advancedBillingEnabled: boolean;
    sortState: TimecardSortState;
    isInViewMode: boolean;
    invoice: InvoiceData;
    invoiceId: number;
    addedInvoiceTimecardIds: number[];
    matterAssignments: NamedEntityMinDto[];
    control: Control<InvoiceMatterForm>;
    validateForm: UseFormHandleSubmit<InvoiceMatterForm>;
    setValue: UseFormSetValue<InvoiceMatterForm>;
    getValues: UseFormGetValues<InvoiceMatterForm>;
    reset: UseFormReset<InvoiceMatterForm>;
    classifiers: Record<string, ClassifierData>;
    handleUpdateInvoiceTimecards: (idList: number[], updatedTimecards: InvoiceTimecardForm[]) => Promise<void>;
    openModalForTimecardId: number | null;
    clearModalRequest: () => void;
    nonBillableBgColor: string | undefined;
    isCredit: boolean;
    isCorrectionInvoice: boolean;
    isSent: boolean;
    isPaid: boolean;
    multiplierTypes: MultiplierType[];
    buttonsDisabled: boolean;
    isTwoDecimalDisplayEnabled: boolean;
    isTwoDecimalEntryEnabled: boolean;
    displayDecimalScale: 1 | 2;
    selectedIds: Set<number>;
    toggleSelection: (id: number) => void;
}

const InvoiceTimecardAssignmentTable = React.memo<InvoiceTimecardAssignmentTableProps>(
    ({
        assignment,
        advancedBillingEnabled,
        sortState,
        isInViewMode,
        invoice,
        invoiceId,
        addedInvoiceTimecardIds,
        matterAssignments,
        control,
        validateForm,
        setValue,
        getValues,
        reset,
        classifiers,
        handleUpdateInvoiceTimecards,
        openModalForTimecardId,
        clearModalRequest,
        nonBillableBgColor,
        isCredit,
        isCorrectionInvoice,
        isSent,
        isPaid,
        multiplierTypes,
        buttonsDisabled,
        isTwoDecimalDisplayEnabled,
        isTwoDecimalEntryEnabled,
        displayDecimalScale,
        selectedIds,
        toggleSelection,
    }) => {
        const invoiceTimecards = useWatch({ control, name: 'invoiceTimecards' });
        const [updateInvoiceTimecard] = useUpdateInvoiceTimecardMutation();
        const listRef = useRef<VariableSizeList>(null);
        const heightCacheByIdRef = useRef<Record<number, number>>({});
        const rafRef = useRef<number>(0);
        const minDirtyIndexRef = useRef<number>(Infinity);

        const idToIndexMapRef = useRef(new Map<number, number>());
        useMemo(() => {
            const newMap = new Map<number, number>();
            if (invoiceTimecards) {
                invoiceTimecards.forEach((tc, i) => newMap.set(tc.id, i));
            }
            const old = idToIndexMapRef.current;
            if (newMap.size !== old.size || [...newMap].some(([k, v]) => old.get(k) !== v)) {
                idToIndexMapRef.current = newMap;
            }
        }, [invoiceTimecards]);

        const timeCardDetails = assignment.timeCardDetails;

        // Only render TCs that already exist in the form state. After add-new-timecards
        // the refetched summary contains the new rows before react-hook-form's `values`
        // effect has propagated them to the form — rendering those rows would pass
        // index=-1 into useWatch and display "undefined EUR/h" until the next render.
        const sortedTimecards = useMemo(() => {
            const map = idToIndexMapRef.current;
            return sortTimecardsByField(timeCardDetails || [], sortState).filter((tc) => map.has(tc.id));
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [timeCardDetails, sortState, invoiceTimecards]);

        const sortedTimecardsRef = useRef(sortedTimecards);
        sortedTimecardsRef.current = sortedTimecards;

        // Advanced billing changes affect row layout, so clear measured heights.
        useEffect(() => {
            heightCacheByIdRef.current = {};
            setMeasuredHeight(null);
            listRef.current?.resetAfterIndex(0, true);
        }, [advancedBillingEnabled]);

        const getItemSize = useCallback((index: number) => {
            const tc = sortedTimecardsRef.current[index];
            if (tc && heightCacheByIdRef.current[tc.id] != null) {
                return heightCacheByIdRef.current[tc.id];
            }
            return DEFAULT_ROW_HEIGHT;
        }, []);

        const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);

        const recalcTotalHeight = useCallback((itemCount: number) => {
            let total = 0;
            for (let i = 0; i < itemCount; i++) {
                const tc = sortedTimecardsRef.current[i];
                total += (tc ? heightCacheByIdRef.current[tc.id] : undefined) ?? DEFAULT_ROW_HEIGHT;
            }
            return total;
        }, []);

        // Membership or order changed (sort, discard, add): react-window's index-keyed
        // offsets and the measured total are both stale, and setRowHeight won't fire for
        // surviving rows whose heights are unchanged. Heights are cached by timecard ID,
        // so the exact total can be recomputed without waiting for rows to re-measure.
        const sortedIdsKey = useMemo(() => sortedTimecards.map((tc) => tc.id).join(','), [sortedTimecards]);
        useEffect(() => {
            listRef.current?.resetAfterIndex(0, true);
            setMeasuredHeight(recalcTotalHeight(sortedTimecardsRef.current.length));
        }, [sortedIdsKey, recalcTotalHeight]);

        const setRowHeight = useCallback(
            (index: number, height: number) => {
                const tc = sortedTimecardsRef.current[index];
                if (!tc) return;
                if (heightCacheByIdRef.current[tc.id] !== height) {
                    heightCacheByIdRef.current[tc.id] = height;
                    minDirtyIndexRef.current = Math.min(minDirtyIndexRef.current, index);
                    if (rafRef.current) {
                        cancelAnimationFrame(rafRef.current);
                    }
                    rafRef.current = requestAnimationFrame(() => {
                        const resetFrom = minDirtyIndexRef.current;
                        minDirtyIndexRef.current = Infinity;
                        listRef.current?.resetAfterIndex(resetFrom, true);
                        const total = recalcTotalHeight(sortedTimecardsRef.current.length);
                        setMeasuredHeight(total);
                    });
                }
            },
            [recalcTotalHeight]
        );

        // Scroll to target row when openModalForTimecardId is set
        useEffect(() => {
            if (openModalForTimecardId != null && sortedTimecards.length > 0) {
                const targetIndex = sortedTimecards.findIndex((tc) => tc.timecard?.id === openModalForTimecardId);
                if (targetIndex >= 0) {
                    listRef.current?.scrollToItem(targetIndex, 'center');
                }
            }
        }, [openModalForTimecardId, sortedTimecards]);

        const renderRow = useCallback(
            (index: number) => {
                const field = sortedTimecards[index];
                if (!field) return null;
                const wIndex = idToIndexMapRef.current.get(field.id) ?? -1;

                return (
                    <InvoiceTimecardTableItem
                        key={field.id}
                        readonly={isInViewMode}
                        index={wIndex}
                        id={field.id}
                        invoiceTimecard={field}
                        timecardForm={invoiceTimecards?.[wIndex]}
                        invoiceId={invoiceId}
                        invoice={invoice}
                        matterAssingmentList={matterAssignments}
                        classifiers={classifiers}
                        isNew={
                            field.timecard !== null ? addedInvoiceTimecardIds.includes(field.timecard.id ?? 0) : false
                        }
                        control={control}
                        validateForm={validateForm}
                        setValue={setValue}
                        getValues={getValues}
                        reset={reset}
                        advancedBillingEnabled={advancedBillingEnabled}
                        handleUpdateInvoiceTimecards={(idList, updatedTimecards) => {
                            void handleUpdateInvoiceTimecards(idList, updatedTimecards);
                        }}
                        shouldOpenModal={field.timecard?.id === openModalForTimecardId}
                        clearModalRequest={clearModalRequest}
                        updateInvoiceTimecard={updateInvoiceTimecard}
                        nonBillableBgColor={nonBillableBgColor}
                        isCredit={isCredit}
                        isCorrectionInvoice={isCorrectionInvoice}
                        isSent={isSent}
                        isPaid={isPaid}
                        multiplierTypes={multiplierTypes}
                        buttonsDisabled={buttonsDisabled}
                        isTwoDecimalDisplayEnabled={isTwoDecimalDisplayEnabled}
                        isTwoDecimalEntryEnabled={isTwoDecimalEntryEnabled}
                        displayDecimalScale={displayDecimalScale}
                        isSelected={selectedIds.has(field.id)}
                        toggleSelection={toggleSelection}
                    />
                );
            },
            [
                sortedTimecards,
                invoiceTimecards,
                isInViewMode,
                invoiceId,
                invoice,
                matterAssignments,
                classifiers,
                addedInvoiceTimecardIds,
                control,
                validateForm,
                setValue,
                getValues,
                reset,
                advancedBillingEnabled,
                handleUpdateInvoiceTimecards,
                openModalForTimecardId,
                clearModalRequest,
                updateInvoiceTimecard,
                nonBillableBgColor,
                isCredit,
                isCorrectionInvoice,
                isSent,
                isPaid,
                multiplierTypes,
                buttonsDisabled,
                isTwoDecimalDisplayEnabled,
                isTwoDecimalEntryEnabled,
                displayDecimalScale,
                selectedIds,
                toggleSelection,
            ]
        );

        const itemData = useMemo(
            () => ({
                setRowHeight,
                renderRow,
            }),
            [setRowHeight, renderRow]
        );

        // Don't render rows until the form is populated — mounting InvoiceTimecardTableItem
        // with index=-1 causes useWatch to subscribe to invalid paths that don't recover cleanly.
        if (!invoiceTimecards || invoiceTimecards.length === 0) {
            return null;
        }

        const itemCount = sortedTimecards.length;

        // Use measured total height when available, otherwise estimate
        const totalHeight = measuredHeight ?? itemCount * DEFAULT_ROW_HEIGHT;
        const listHeight = Math.min(totalHeight, MAX_LIST_HEIGHT);

        return (
            <VariableSizeList
                ref={listRef}
                height={listHeight}
                width="100%"
                itemCount={itemCount}
                itemSize={getItemSize}
                overscanCount={OVERSCAN_COUNT}
                itemData={itemData}
            >
                {MeasuredRow}
            </VariableSizeList>
        );
    }
);

export default InvoiceTimecardAssignmentTable;
